import React, { FC } from 'react';
import { SiteOpenDay } from 'core/sites/types';
import './openHoursList.scss';

// 12-hour clock, matching how a retail store's opening times read.
const formatTime = (time: string | null | undefined): string => {
    if (!time || typeof time !== 'string') return '-';
    const parts = time.split(':');
    let hours = parseInt(parts[0] ?? '0', 10);
    const minutes = parts[1] ?? '00';
    const period = hours >= 12 ? 'PM' : 'AM';
    if (hours === 0) hours = 12;
    else if (hours > 12) hours -= 12;
    return `${hours}:${minutes} ${period}`;
};

const isValidDay = (openDay: unknown): openDay is SiteOpenDay => (
    openDay != null && typeof openDay === 'object' && typeof (openDay as SiteOpenDay).day === 'string'
);

// A 24/7 site comes back as a full week of 07:00 to 07:00, which reads as a
// zero-length day rather than a round-the-clock one.
const formatHours = (openDay: SiteOpenDay): string => (
    openDay.startTime && openDay.startTime === openDay.endTime
        ? '24 hours'
        : `${formatTime(openDay.startTime)} - ${formatTime(openDay.endTime)}`
);

/**
 * The days a site is open, one row per day, laid out like the retail opening
 * times. Renders a dash when there is nothing to show.
 */
const OpenHoursList: FC<{ openDays: SiteOpenDay[] }> = ({ openDays }) => {
    const validDays = openDays.filter(isValidDay);
    if (validDays.length === 0) return <div className="open-hours-empty">-</div>;

    return (
        <div className="open-hours-list">
            {validDays.map((openDay) => (
                <div className="open-hours-row" key={`${openDay.day}-${openDay.startTime}`}>
                    <span className="open-hours-day">{openDay.day}:</span>
                    <span className="open-hours-times">{formatHours(openDay)}</span>
                </div>
            ))}
        </div>
    );
};

export default OpenHoursList;
